/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import moment from 'moment-timezone';
import {editingScheduledReport, setCreateScheduledReport, setEditScheduledReport} from '@ion/applications/src/pages/analytics/store';
import {GenericObject} from '@ion/core/src/types/entities';
import {isEmpty, stringToNumber} from '@ion/core/src/utils/StringUtil';
import {FormState} from '@ion/dls/src/hooks/useFormState';

/**
 * Enum representing the fields of a item.
 */
export enum ScheduledReportEditorFields {
  Id = 'id',
  Name = 'name',
  SftpServer = 'sftpServer',
  Priority = 'priority',
  FileUpload = 'fileUpload',
  FileNameTemplate = 'fileNameTemplate',
  EmailSubject = 'emailSubject',
  EmailBody = 'emailBody',
  Compressed = 'compressed',
  Schedule = 'schedule',
  Dashboard = 'dashboard',
  Emails = 'emails',
  ErrorEmails = 'errorEmails',
  ReportType = 'reportType',
  CustomFiltersEnabled = 'customFiltersEnabled',
  ReportTypePdf = 'pdf',
  ReportTypeCsv = 'csv',
  ReportTypeXlsCombined = 'xlsCombined',
  ArchiveGroup = 'archiveGroup',
  OverrideExistingFiles = 'overrideExistingFiles',
  DeliveryOptions = 'deliveryOptions',
  CustomizeEmailMessage = 'customizeEmailMessage',
  Timezone = 'timezone',
  ScheduleType = 'scheduleType',
  RepeatEveryDay = 'repeatEveryDay',
  MonthDays = 'monthDays',
  DaysOfWeek = 'daysOfWeek',
  StartTime = 'startTime',
  EndTime = 'endTime',
  StartDate = 'startDate',
  EndDate = 'endDate',
  Directory = 'directory',
  OverwriteExisting = 'overwriteExisting',
  SeasonalSchedule = 'seasonalSchedule',
  SeasonalMonths = 'seasonalMonths'
};
  
// defines the route event target type definition ...
export interface TargetType {
  name: ScheduledReportEditorFields,
  value: unknown;
  required?: boolean
}

export enum IntervalTypes {
  day = 'day',
  week = 'week',
  month = 'month',
  once = 'once',
  hour = 'hour'
};

export enum IntervalDays {
  Once = 0,
  Weekly = 7,
  Monthly = 30
};

const convertDateTime = ({
  selectedDate,
  startHour,
  startMinute,
  timezone
}) => {
  const zonedDate = moment.tz({
    year   : selectedDate.getFullYear(),
    month  : selectedDate.getMonth(),
    date   : selectedDate.getDate(),
    hour   : startHour,
    minute : startMinute
  }, timezone);

  return zonedDate.format();
};

// formats the schedule hour and minute as a zero-padded HH:mm time, defaulting to 12:00 ...
const formatScheduleTime = (hour?: number | string, minute?: number | string): string => (
  `${String(hour ?? '12').padStart(2, '0')}:${String(minute ?? '00').padStart(2, '0')}`
);

/**
 * Converts a item object into a structured form state.
 *
 * @param item - The item object to deserialize.
 * @returns    - The deserialized form state.
 */
export const deserializeScheduledReport = (scheduledReport) => {
  const date = new Date();
  const {
    id,
    name,
    priority,
    fileNameTemplate,
    emailSubject,
    emailBody,
    compressed,
    emails,
    errorEmails,
    reportType,
    sftpServer,
    fileUpload,
    reportTypePdf,
    reportTypeCsv,
    reportTypeXlsCombined,
    customFiltersEnabled,
    archiveGroup,
    overrideExistingFiles,
    customizeEmailMessage,
    schedule,
    timezone,
    scheduleType,
    repeatEveryDay,
    monthDays,
    daysOfWeek,
    startTime,
    endTime,
    startDate,
    endDate,
    dashboard,
    directory,
    overwriteExisting
  } = scheduledReport || {};
  const normalizedEmails = Array.isArray(emails) ? emails.join(',') : emails;
  const normalizedErrorEmails = Array.isArray(errorEmails) ? errorEmails.join(',') : errorEmails;
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let isoStartDate = '';
  let isoEndDate = '';
  if (scheduledReport && Object.keys(scheduledReport).length > 0) {
    isoStartDate = moment.utc(schedule?.startDate).tz(schedule?.timezone).startOf('day').toDate();
    isoEndDate = schedule?.endDate ? moment.utc(schedule?.endDate).tz(schedule?.timezone).startOf('day').toDate() : undefined;
  }
  return {
    [ScheduledReportEditorFields.Id] : {
      name     : ScheduledReportEditorFields.Id,
      value    : id,
      required : false
    },
    [ScheduledReportEditorFields.Name] : {
      name     : ScheduledReportEditorFields.Name,
      value    : name,
      required : true
    },
    [ScheduledReportEditorFields.Priority] : {
      name     : ScheduledReportEditorFields.Priority,
      value    : priority || 'normal',
      required : false
    },
    [ScheduledReportEditorFields.EmailSubject] : {
      name     : ScheduledReportEditorFields.EmailSubject,
      value    : emailSubject,
      required : false
    },
    [ScheduledReportEditorFields.EmailBody] : {
      name     : ScheduledReportEditorFields.EmailBody,
      value    : emailBody,
      required : false
    },
    [ScheduledReportEditorFields.Compressed] : {
      name     : ScheduledReportEditorFields.Compressed,
      value    : compressed || false,
      required : false
    },
    [ScheduledReportEditorFields.Schedule] : {
      name  : ScheduledReportEditorFields.Schedule,
      value : {
        timezone    : timezone?.value,
        every       : schedule?.every,
        frequency   : schedule?.frequency,
        daysOfWeek  : schedule?.daysOfWeek,
        months      : schedule?.months,
        startDate   : moment(schedule?.startDate).clone().tz(systemTimezone).toDate(),
        endDate     : schedule?.endDate ? moment(schedule?.endDate).clone().tz(systemTimezone).toDate() : undefined,
        startHour   : schedule?.startHour || 0,
        startMinute : schedule?.startMinute || 0,
        endHour     : schedule?.endHour,
        endMinute   : schedule?.endMinute
      },
      required : true
    },
    [ScheduledReportEditorFields.SeasonalSchedule] : {
      name  : ScheduledReportEditorFields.SeasonalSchedule,
      value : !!schedule?.months
    },
    [ScheduledReportEditorFields.CustomFiltersEnabled] : {
      name     : ScheduledReportEditorFields.CustomFiltersEnabled,
      value    : dashboard?.customFiltersEnabled || false,
      required : false
    },
    [ScheduledReportEditorFields.Dashboard] : {
      name  : ScheduledReportEditorFields.Dashboard,
      value : {
        id                   : scheduledReport?.dashboard?.id,
        customFiltersEnabled : customFiltersEnabled
      },
      required : true
    },
    [ScheduledReportEditorFields.Emails] : {
      name     : ScheduledReportEditorFields.Emails,
      value    : normalizedEmails,
      required : true
    },
    [ScheduledReportEditorFields.ErrorEmails] : {
      name     : ScheduledReportEditorFields.ErrorEmails,
      value    : normalizedErrorEmails,
      required : false
    },
    [ScheduledReportEditorFields.ReportTypePdf] : {
      name     : ScheduledReportEditorFields.ReportTypePdf,
      value    : reportType?.pdf || false,
      required : false
    },

    [ScheduledReportEditorFields.ReportTypeCsv] : {
      name     : ScheduledReportEditorFields.ReportTypeCsv,
      value    : reportType?.csv || false,
      required : false
    },
    [ScheduledReportEditorFields.ReportTypeXlsCombined] : {
      name     : ScheduledReportEditorFields.ReportTypeXlsCombined,
      value    : reportType?.xlsCombined || false,
      required : false
    },
    [ScheduledReportEditorFields.ReportType] : {
      name  : ScheduledReportEditorFields.ReportType,
      value : {
        pdf         : reportType?.pdf || false,
        csv         : reportType?.csv || false,
        xlsCombined : reportType?.xlsCombined || false
      },
      required : false
    },
    [ScheduledReportEditorFields.ArchiveGroup] : {
      name     : ScheduledReportEditorFields.ArchiveGroup,
      value    : archiveGroup,
      required : false
    },
    [ScheduledReportEditorFields.FileUpload] : {
      name     : ScheduledReportEditorFields.FileUpload,
      value    : fileUpload,
      required : false
    },
    [ScheduledReportEditorFields.SftpServer] : {
      name     : ScheduledReportEditorFields.SftpServer,
      value    : sftpServer || fileUpload?.name || {},
      required : false
    },
    [ScheduledReportEditorFields.OverwriteExisting] : {
      name     : ScheduledReportEditorFields.OverwriteExisting,
      value    : fileUpload?.overwriteExisting,
      required : false
    },
    [ScheduledReportEditorFields.Directory] : {
      name     : ScheduledReportEditorFields.Directory,
      value    : fileUpload?.directory,
      required : false
    },
    [ScheduledReportEditorFields.OverrideExistingFiles] : {
      name     : ScheduledReportEditorFields.OverrideExistingFiles,
      value    : overrideExistingFiles,
      required : false
    },
    [ScheduledReportEditorFields.CustomizeEmailMessage] : {
      name     : ScheduledReportEditorFields.CustomizeEmailMessage,
      value    : (editingScheduledReport?.value && !isEmpty(emailSubject)),
      required : false
    },
    [ScheduledReportEditorFields.FileNameTemplate] : {
      name     : ScheduledReportEditorFields.FileNameTemplate,
      value    : fileNameTemplate || '',
      required : false
    },
    [ScheduledReportEditorFields.Timezone] : {
      name     : ScheduledReportEditorFields.Timezone,
      value    : schedule?.timezone,
      required : false
    },
    [ScheduledReportEditorFields.ScheduleType] : {
      name     : ScheduledReportEditorFields.ScheduleType,
      value    : schedule?.frequency || 'once',
      required : false
    },
    [ScheduledReportEditorFields.RepeatEveryDay] : {
      name     : ScheduledReportEditorFields.RepeatEveryDay,
      value    : schedule?.every || null,
      required : false
    },
    [ScheduledReportEditorFields.MonthDays] : {
      name     : ScheduledReportEditorFields.MonthDays,
      value    : schedule?.frequency === 'month' ? schedule?.monthDays?.join() : null,
      required : false
    },
    [ScheduledReportEditorFields.DaysOfWeek] : {
      name     : ScheduledReportEditorFields.DaysOfWeek,
      value    : schedule?.frequency === 'week' ? schedule?.daysOfWeek || [] : [],
      required : false
    },
    [ScheduledReportEditorFields.SeasonalMonths] : {
      name     : ScheduledReportEditorFields.SeasonalMonths,
      value    : schedule?.months || [],
      required : false
    },
    [ScheduledReportEditorFields.StartTime] : {
      name     : ScheduledReportEditorFields.StartTime,
      value    : formatScheduleTime(schedule?.startHour, schedule?.startMinute),
      required : false
    },
    [ScheduledReportEditorFields.EndTime] : {
      name     : ScheduledReportEditorFields.EndTime,
      value    : formatScheduleTime(schedule?.endHour, schedule?.endMinute),
      required : false
    },
    [ScheduledReportEditorFields.StartDate] : {
      name     : ScheduledReportEditorFields.StartDate,
      value    : isoStartDate,
      required : false
    },
    [ScheduledReportEditorFields.EndDate] : {
      name     : ScheduledReportEditorFields.EndDate,
      value    : schedule?.endDate ? isoEndDate : undefined,
      required : false
    }
  };
};

/**
 * Serializes the form state into a format suitable for API submission.
 *
 * @param state - The form state to serialize.
 * @returns     - The serialized item object.
 */
export const serializeScheduledReport = (state: FormState, selectedDashboardReport: any) => {
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const startTime = state?.startTime?.value;
  const endTime = state?.endTime?.value;
  const scheduleType = state?.scheduleType?.value;
  const isSeasonal = state?.seasonalSchedule?.value;
  const [startHour, startMinute] = startTime?.split(':');
  const [endHour, endMinute] = endTime ? endTime?.split(':') : startTime?.split(':');
  const timezone = state?.timezone?.value?.value || 'UTC';
  const zonedStartDate = moment(state?.startDate?.value).clone().tz(timezone).toDate();
  const zonedEndDate = moment(state?.endDate?.value).clone().tz(timezone).toDate();
  const reportTypeValue = state?.reportType?.value || {};
  const sftpConfig = state.sftpServer?.value || {};
  const serialized: GenericObject = {};
  const startDate = convertDateTime({
    selectedDate : zonedStartDate,
    startHour,
    startMinute,
    timezone
  });
  const endDate = convertDateTime({
    selectedDate : zonedEndDate,
    startHour    : endHour,
    startMinute  : endMinute,
    timezone
  });
  for (const [key, {value}] of Object.entries(state)) {
    switch (key) {
      case ScheduledReportEditorFields.Emails:
        serialized[key] = Array.isArray(value) ? value : value?.split(',').map(emails => emails.trim());
        break;
      case ScheduledReportEditorFields.ErrorEmails:
        serialized[key] = Array.isArray(value) ? value : value?.split(',').map(errorEmails => errorEmails.trim());
        break;
      case ScheduledReportEditorFields.Schedule:
        serialized[key] = {
          timezone    : timezone,
          every       : state?.repeatEveryDay?.value,
          frequency   : state?.scheduleType?.value,
          daysOfWeek  : state?.daysOfWeek?.value,
          monthDays   : (state?.monthDays?.value)?.split(',').map(monthDay => stringToNumber(monthDay.trim())),
          startDate   : startDate,
          startHour   : startHour || 0,
          startMinute : startMinute || 0
        };
        if (scheduleType === 'hour') {
          const [endHour, endMinute] = endTime?.split(':');
          serialized[key].endHour = endHour;
          serialized[key].endMinute = endMinute;
        }
        if (state?.endDate?.value) {
          serialized[key].endDate = endDate;
        }
        if (isSeasonal) {
          serialized[key].months = state?.seasonalMonths?.value;
        }
        break;
      case ScheduledReportEditorFields.Dashboard:
        serialized[key] = {
          id                   : selectedDashboardReport?.oid,
          customFiltersEnabled : state?.customFiltersEnabled?.value
        };
        break;
      case ScheduledReportEditorFields.FileNameTemplate:
        serialized[key] = state?.fileNameTemplate.value;
        break;
      case ScheduledReportEditorFields.ReportType:
        serialized[key] = {
          pdf         : state?.pdf?.value ?? reportTypeValue?.pdf,
          csv         : state?.csv?.value ?? reportTypeValue?.csv,
          xlsCombined : state?.xlsCombined?.value ?? reportTypeValue?.xlsCombined
        };
        break;
      case ScheduledReportEditorFields.EmailSubject:
        serialized[key] = state?.emailSubject?.value;
        break;
      case ScheduledReportEditorFields.EmailBody:
        serialized[key] = state?.emailBody?.value;
        break;
      case ScheduledReportEditorFields.FileUpload:
        serialized[key] = {
          id                : sftpConfig?.id,
          name              : sftpConfig?.name,
          protocol          : 'sftp',
          authType          : 'privateKey',
          host              : sftpConfig?.host,
          port              : sftpConfig?.port,
          username          : sftpConfig?.username,
          password          : '',
          privateKey        : '',
          directory         : state?.directory?.value || '/root/ion',
          overwriteExisting : state?.overwriteExisting?.value
        };
        break;
      default :
        serialized[key] = value;
        break;
    }
  }
  delete serialized[ScheduledReportEditorFields.Timezone];
  delete serialized[ScheduledReportEditorFields.RepeatEveryDay];
  delete serialized[ScheduledReportEditorFields.DaysOfWeek];
  delete serialized[ScheduledReportEditorFields.StartTime];
  delete serialized[ScheduledReportEditorFields.EndTime];
  delete serialized[ScheduledReportEditorFields.StartDate];
  delete serialized[ScheduledReportEditorFields.EndDate];
  delete serialized[ScheduledReportEditorFields.CustomFiltersEnabled];
  if (serialized[ScheduledReportEditorFields.ScheduleType] === 'once') {
    delete serialized[ScheduledReportEditorFields.Schedule]?.daysOfWeek;
    delete serialized[ScheduledReportEditorFields.Schedule]?.monthDays;
    delete serialized[ScheduledReportEditorFields.Schedule]?.endDate;
  }
  if (serialized[ScheduledReportEditorFields.ScheduleType] === 'month') {
    delete serialized[ScheduledReportEditorFields.Schedule]?.daysOfWeek;
  }
  if (!(serialized[ScheduledReportEditorFields.Schedule]?.endDate)) {
    delete serialized[ScheduledReportEditorFields.Schedule]?.endDate;
  }
  delete serialized[ScheduledReportEditorFields.SeasonalSchedule];
  delete serialized[ScheduledReportEditorFields.SeasonalMonths];
  delete serialized[ScheduledReportEditorFields.ScheduleType];
  delete serialized[ScheduledReportEditorFields.SftpServer];

  if (!serialized[ScheduledReportEditorFields.FileUpload]?.name) {
    delete serialized[ScheduledReportEditorFields.FileUpload];
  }

  editingScheduledReport?.value ? setEditScheduledReport(serialized) : setCreateScheduledReport(serialized);
  
  return serialized;
};
